import { Component, Inject, Input, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
// services
import { AppStateService } from '../../../shared/services/app-state.service';
import { AirportCopyService } from '../../../shared/services/app-airport-copy.service';
import { CopyService } from '../../../shared/services/app-copy.service';
import { DataStorageService } from '../../../shared/services/data-storage.service';
import { BookingService } from '../../../booking/services/booking.service';
import { AppLoaderService } from '../../../shared/services/app-loader.service';
// models
import { AppState } from '../../models/app-state.interface';
import { PageLoaderComponent } from 'app/booking/components/page-loader/page-loader.component';
import { updateCustomerDetails, updateCustomerHasMarketingEnrolment } from 'app/booking/reducers/booking.actions';
import { resetOptionalDataFlybackReducer, updateFlybackCustomerDetails, updateFlybackCustomerHasMarketingEnrolment } from 'app/booking/reducers/flyback.reducer';
import { FormsModule } from '@angular/forms';
import { GetFlybackDetailsComponent } from 'app/booking/components/get-flyback-details/get-flyback-details.component';
import { GetFlybackFlightResultsComponent } from 'app/booking/components/get-flyback-flight-results/get-flyback-flight-results.component';
import { FlybackFlightConfirmationComponent } from 'app/booking/components/flyback-flight-confirmation/flyback-flight-confirmation.component';




@Component({
  selector: 'app-flyback',
  standalone: true,
  imports: [CommonModule, FormsModule ,PageLoaderComponent, GetFlybackDetailsComponent, GetFlybackFlightResultsComponent, FlybackFlightConfirmationComponent],
  templateUrl: './flyback.component.html',
  styleUrls: ['./flyback.component.scss']
})
export class FlybackComponent implements OnInit, OnDestroy {

  @Input() showFlyBackSection = false;

  private _flybackReducerSubscription: Subscription
  private _routeParamsSubscription: Subscription;
  private _bookingRefreshed: Subscription;
  private _loadingSubscription: Subscription;
  private _userLanguageSubscription: Subscription;

  private _currentBooking;

  public bookingReference: string = null;
  public state: AppState;
  public step = 0;
  public dataBookingLoaded: Boolean = false;
  public dataFlightLoaded: Boolean = false;
  public copyLoaded: Boolean = false;
  public languageCode = '';
  public selectedLanguage = '';
  public flightDirection: number;
  public appCopy;
  public appAirportCopy;
  public flightData;
  public bookingDetails;

  constructor(
    private _bookingService: BookingService,
    private _activatedRoute: ActivatedRoute,
    private _appStateService: AppStateService,
    private _appStore: Store<AppState>,
    private _dataStorageService: DataStorageService,
    private _airportCopyService: AirportCopyService,
    private _copyService: CopyService,
    private _appLoaderService: AppLoaderService,
    private _router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this._appLoaderService.hideLoader();
      // subscribe to router query string
      this._routeParamsSubscription = this._activatedRoute.queryParams
        .subscribe((params: any) => {
          if (typeof params.ref !== 'undefined') {
            this.bookingReference = params.ref;
          }
        },
          err => console.log(err));

      this.state = this.getState(this._appStore);
      // if store already loaded
      // else get from api

      if (this.state.loadedStatusReducer.flightLoaded && this.state.loadedStatusReducer.currentBookingLoaded) {
        this.setViewValues();
      } else {
        this._appStateService.getBookingStateFromAPI(false, true);
        // if calling api
        // wait for store to refresh

        this._bookingRefreshed = this._appStateService.completeBookingRefreshed$
          .subscribe(res => {
            if (res) {
              this.setViewValues();
            }
          });
      }



    }


  }

  getCurrentBookingReducer(s: AppState) {
    const d2dReducer = s.oldBookingInfoReducer.d2dReducer;
    const bookingProduct = d2dReducer.currentBookingProduct;
    return bookingProduct === "abc" ? d2dReducer.outboundBooking.currentBookingReducer : d2dReducer.inboundBooking.currentBookingReducer;
  }

  getBookingReducer(s: AppState) {
    const d2dReducer = s.oldBookingInfoReducer.d2dReducer;
    const bookingProduct = d2dReducer.currentBookingProduct;
    return bookingProduct === "abc" ? d2dReducer.outboundBooking.bookingReducer : d2dReducer.inboundBooking.bookingReducer;
  }

  getFlightReducer(s: AppState) {
    const d2dReducer = s.oldBookingInfoReducer.d2dReducer;
    const bookingProduct = d2dReducer.currentBookingProduct;
    return bookingProduct === "abc" ? d2dReducer.outboundBooking.flightReducer : d2dReducer.inboundBooking.flightReducer;
  }

  setViewValues() {
    this.state = this.getState(this._appStore);

    this._currentBooking = this.getCurrentBookingReducer(this.state).currentBooking;

    const customerDetails = {
      firstName: this._currentBooking.bookingUser.firstName,
      lastName: this._currentBooking.bookingUser.lastName,
      emailAddress: this._currentBooking.bookingUser.emailAddress
    };

    this._appStore.dispatch(updateCustomerDetails({ payload: customerDetails }));
    this._appStore.dispatch(updateCustomerHasMarketingEnrolment({ payload: this._currentBooking.hasMarketingEnrolment }));

    this._appStore.dispatch(updateFlybackCustomerDetails({ payload: customerDetails }));
    this._appStore.dispatch(updateFlybackCustomerHasMarketingEnrolment({ payload: this._currentBooking.hasMarketingEnrolment }));


    if (this._activatedRoute.snapshot?.url[0]?.path === 'fly-back-details') {
      this._appStore.dispatch(resetOptionalDataFlybackReducer());


      const flybackReducer = this.state.flybackReducer;

      if (flybackReducer.eligibleProducts === null) {
        this._bookingService.reCheckFlyBackEligibility().then((res: any) => {

          if (res.flyBackProducts && res.flyBackProducts.length > 0) {
            this.saveAndSetData();
          } else {
            this._router.navigate(['complete'], { queryParams: { ref: this.bookingReference } });
            return;
          }
        });

      } else {
        this.saveAndSetData();
      }

    } else {
      this.saveAndSetData();
    }

  }

  private saveAndSetData() {
    this._dataStorageService.saveData();

    this.setBookingStoreData(this.state);
    this.setFlightStoreData(this.state);
    this.setDataSubscriptions();
  }

  setBookingStoreData(s: AppState) {
    this.dataBookingLoaded = true;
    this.bookingDetails = this.getBookingReducer(s).bookingDetails;
    this.flightDirection = this.bookingDetails.journeyLegDetails[0].journeyDirection;
  }

  setFlightStoreData(s: AppState) {
    this.dataFlightLoaded = true;
    const flightReducer = this.getFlightReducer(this.state);
    this.flightData = flightReducer.flightDetails.flightResponse;
  }

  setDataSubscriptions() {
    this._loadingSubscription = this._appStore.select('loadedStatusReducer')
      .subscribe(res => {

        this._userLanguageSubscription = this._appStore.select('copyReducer')
          .subscribe((res: any) => {
            if (res.languageCode !== this.languageCode) {
              this.languageCode = res.languageCode;
            }
            this.selectedLanguage = res.language;
            this.languageCode = res.languageCode;
          })


        if (res.copyLoaded && res.rulesCopyLoaded) {

          if (isPlatformBrowser(this.platformId)) {
            this.getCopy();
            this.getAirportCopy();
            this.copyLoaded = true;
          }
        }

      });

    this._flybackReducerSubscription = this._appStore.select('flybackReducer')
      .subscribe(data => {
        this.step = data.currentStep;
      });
  }

  getAirportCopy() {
    this.appAirportCopy = this._airportCopyService.getAirportsCopy();
  }

  getCopy() {
    const panelHeaderCopy = this._copyService.getCopyBySection(
      'OTHER',
      'flyback',
      ['panelHeader']
    );

    this.appCopy = {
      panelHeader: panelHeaderCopy[0]
    };

  }

  getState(store: Store<AppState>): AppState {
    let state: AppState;
    store.pipe(take(1))
      .subscribe(s => state = s);
    return state;
  }

  ngOnDestroy(): void {
    if (this._flybackReducerSubscription) { this._flybackReducerSubscription.unsubscribe(); }
    if (this._bookingRefreshed) { this._bookingRefreshed.unsubscribe(); }
    if (this._routeParamsSubscription) { this._routeParamsSubscription.unsubscribe(); }
    if (this._loadingSubscription) { this._loadingSubscription.unsubscribe(); }
    if (this._userLanguageSubscription) { this._userLanguageSubscription.unsubscribe(); }
  }
}
